import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrmNotes, CrmNote } from '../../crm-notes/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentNotesProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentNotes: React.FC<RecentNotesProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: notes, isLoading } = useCrmNotes();
  const arr = Array.isArray(notes) ? notes : [];

  const items = React.useMemo(() => {
    return [...arr]
      .filter((n: CrmNote) => !n.noteArchived)
      .sort((a: CrmNote, b: CrmNote) => {
        const ta = new Date(a.noteUpdatedAt || a.noteCreatedAt || 0).getTime();
        const tb = new Date(b.noteUpdatedAt || b.noteCreatedAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<CrmNote>
      title="Recent Notes"
      viewAllPath="/crm_notes"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No recent notes"
      getItemKey={(n) => n.noteId}
      embedded={embedded}
      renderItem={(note) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/crm_notes/${note.noteId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/crm_notes/${note.noteId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {note.noteDescr?.slice(0, 80) || 'Note'}
              {(note.noteDescr?.length ?? 0) > 80 ? '…' : ''}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {note.client?.clientName && `${note.client.clientName} · `}
              {note.noteUpdatedAt || note.noteCreatedAt
                ? new Date(
                    note.noteUpdatedAt || note.noteCreatedAt || ''
                  ).toLocaleDateString('en-US')
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentNotes;
